(ns ycweb.app.admin.productsync.import
  (:require
   [rum.core :as rum]
   [ycweb.app.admin.productsync.productsync :as productsync]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.flow :as flow]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.components.tables :as tables]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.transitions :as t]
   [ycweb.util.comms :as comms]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

;//=====================================================================//
;; This is the final step, showing you which products have been successfully
;; synced to our admin dashboard / database (pulls through row structure from
;; component above).

(rum/defc product-import < rum/reactive cmn/cursors [app-atom]
  (let [process-atom (cmn/process-cursor app-atom)
        uploaded-images-atom (rum/cursor process-atom [:product-sync/imported-images])
        saved-images-idx (u/index-by :variationid (cmn/curact uploaded-images-atom [:saved-images]))
        import-data (cmn/curact process-atom [:product-sync/import])
        import-type (cmn/curact process-atom [:product-sync/import-type])
        products (:content-variations import-data)
        batch-id (:batch-id import-data)
        products-by-content-id (u/index-by :content-id products)
        products-by-brand-id (u/index-by :brand-id products)
        checked-atom (rum/cursor-in process-atom [:product-sync/import :checked])
        all-checked
        (every?
          (fn [content-id]
            (get (rum/react checked-atom) content-id))
          (keys products-by-content-id))]

    [:div.admin-react-wrapper__content
     (headings/heading "Done \uD83C\uDF89\uD83D\uDD25\uD83D\uDD90" {:type :h1 :size :large})
     ; We probably want to make this use the default feedback CLJC component
     [:div {:class (u/bem :feedback [:success :has-shadow :align-left])}
      [:div.feedback__icon [:i {:class (u/bem :icon [:success :inline])}]]
      [:div.feedback__message
       [:h4.feedback-title "Product content imported successfully"]
       [:p.feedback-description
        "You've successfully imported the content and your products are ready to edit. Links are provided in the list below."]
       [:p.feedback-description
        (str "Import Batch ID: " batch-id)]]]

     (tables/table ""
       [{:title "Product Image" :key :vid
         :component-fn
         (fn [vid]
           (cond
             (u/presence (:public_id (get saved-images-idx vid)))
             [:img
              {:src (u/cloudinary (:public_id (get saved-images-idx vid)) {:w 64 :h "full"})
               :alt ""}]

             :else
             [:div
              [:div.u-block-center.u-max-width-48
               [:i.u-animate-spin
                {:class (u/bem :icon [:loading :is-dark :large :block])}]]]))}
        {:title "Product Name" :key :content-id
         :component-fn
         (fn [content-id]
           [:a
            {:href (str "/admin/pages_edit.htm?overrideincludes=popup&sm[e1][action]=contentedit&sm[e1][cntid]=" content-id),
             :target "_blank"}
            (:name (get products-by-content-id content-id))])}
        {:title "Brand" :key :brand-id
         :component-fn
         (fn [brand-id]
           [:a
            {:href (str "/admin/pages.htm?sm[c1][category]=" brand-id)
             :target "_blank"}
            (:brand (get products-by-brand-id brand-id))])}
        {:title "SKU Code" :key :sku-code}
        {:title "Barcode" :key :barcode}
        {:title "Size / Feature (Feature 1)" :key :size}
        {:title "Colour (Feature 2)" :key :colour}
        {:title "View" :key :content-id
         :component-fn
         (fn [content-id]
           [:a {:href (str "/products.htm?id=" content-id), :target "_blank"} "View product"])}
        {:title "Edit" :key :content-id
         :component-fn
         (fn [content-id]
           [:a
            {:href
             (str "/admin/pages_edit.htm?overrideincludes=popup&sm[e1][action]=contentedit&sm[e1][cntid]=" content-id)
             :target "_blank"} "Edit product"])}
        {:title "Checked" :key :content-id
         :component-fn
         (fn [content-id]
           (forms/checkbox "" (rum/cursor checked-atom content-id)))}]
       products
       {})

     [:div
      (buttons/button-web "Done"
        (merge
          {:style :primary
           :size :default
           :disabled (not all-checked)}
          (urlstate/href-on-click
            #(do
               (cond
                 all-checked
                 (grid/dispatch-action app-atom :admin.product-sync/import-finish
                   {:import-type import-type
                    :content-ids (keys products-by-content-id)
                    :variation-ids (map :vid products)
                    :airtable-ids (keep :airtable-id products)})

                 :else
                 (toaster/open-toast app-atom :error "Please check all imported products" {}))))))]
     (toaster/loading-overlay app-atom :admin.product-sync/import)]))

(defmethod flow/render-screen :admin.product-sync/import [app-atom _]
  (product-import app-atom))

(defmethod t/transition-open :admin.product-sync/import [app-atom _ _]
  (let [process-atom (cmn/process-cursor app-atom)
        import-atom (rum/cursor process-atom [:product-sync/import])
        import-progress-options-atom (rum/cursor-in import-atom [:progress-options])
        import-progress-atom (rum/cursor-in import-atom [:progress])
        import-progress-text-atom (rum/cursor-in import-atom [:progress-text])
        summary-overview (cmn/curact process-atom [:product-sync/summary :summary])
        summary (:summary summary-overview)
        batch-id (:batch-id summary-overview)
        products (:products summary-overview)]

    (when-not
      (and
        (u/presence summary)
        (u/presence batch-id)
        (u/presence products))
      (urlstate/redirect app-atom "product-sync"))

    (reset! import-progress-atom "")
    (reset! import-progress-text-atom "Uploading products")
    (reset! import-progress-options-atom
      {:content
       {:msg "Saving Content: "}
       :content_variations
       {:msg "Saving Content Variations: "}
       :change-request
       {:msg "Submitting Price change request"}
       :image
       {:msg "Images sent to the queue: "}
       :product-update
       {:msg "Updating Products: "}
       :cache-invalidation
       {:msg "Invalidating cache: "}})

    (toaster/open-toast app-atom :processing
      (productsync/reactive-message import-progress-text-atom import-progress-atom)
      {:closetime 2000000})
    (comms/send :admin.product-sync/import
      {:summary summary
       :batch-id batch-id
       :products (remove :variation/errors products)})))

(defmethod comms/receive :admin.product-sync/import [app-atom {:keys [content-variations] :as import-response}]
  (let [process-atom (cmn/process-cursor app-atom)
        import-atom (rum/cursor process-atom :product-sync/import)
        variation-ids (map :vid content-variations)]

    (toaster/close-toast app-atom)
    (grid/dispatch-action app-atom :admin.product-sync/check-imported-images {:variation-ids variation-ids})
    (reset! import-atom
      (assoc import-response
        :checked {}))))

(defmethod comms/receive :admin.product-sync/import-update-progress [app-atom {:keys [key current total]}]
  (let [process-atom (cmn/process-cursor app-atom)
        import-atom (rum/cursor process-atom [:product-sync/import])
        import-progress-options-atom (rum/cursor-in import-atom [:progress-options])
        import-progress-atom (rum/cursor-in import-atom [:progress])
        import-progress-text-atom (rum/cursor-in import-atom [:progress-text])
        current-option (get @import-progress-options-atom key)]
    (reset! import-progress-text-atom (:msg current-option))
    (reset! import-progress-atom
      (if
        (and current total)
        (str current "/" total)
        ""))
    (swap! import-progress-options-atom
      assoc key
      {:msg (:msg current-option)
       :total total
       :current current})))

(defn check-imported-images [variation-ids]
  #?(:cljs
     (.setInterval js/window
       #(comms/send :admin.product-sync/check-imported-images {:variation-ids variation-ids})
       3000)))

(defn clear-interval [interval-id]
  #?(:cljs
     (.clearInterval js/window interval-id)))

(defmethod grid/action :admin.product-sync/check-imported-images [app-atom {:keys [variation-ids]}]
  (let [process-atom (cmn/process-cursor app-atom)
        uploaded-images-atom (rum/cursor process-atom [:product-sync/imported-images])
        interval-id (check-imported-images variation-ids)]
    (swap! uploaded-images-atom assoc :interval-id interval-id)))

(defmethod comms/receive :admin.product-sync/check-imported-images [app-atom {:keys [saved-images]}]
  (let [process-atom (cmn/process-cursor app-atom)
        uploaded-images-atom (rum/cursor process-atom [:product-sync/imported-images])
        interval-id (cmn/curact uploaded-images-atom [:interval-id])
        summary-overview (cmn/curact process-atom [:product-sync/summary :summary])
        products (remove :variation/errors (:products summary-overview))
        expected-images-count
        (reduce
          #(+ %1 (count (:variation/image-links %2)))
          0
          products)
        import-complete (= expected-images-count (count saved-images))]

    (swap! uploaded-images-atom assoc :saved-images saved-images)
    (when import-complete
      (clear-interval interval-id))))

(defmethod grid/action :admin.product-sync/import-finish [app-atom config _]
  (toaster/open-toast app-atom :processing "Updating Airtable Records" {})
  (comms/send :admin.product-sync/import-finish
    {:import-type (:import-type config)
     :content-ids (:content-ids config)
     :variation-ids (:variation-ids config)
     :airtable-ids (:airtable-ids config)}))

(defmethod comms/receive :admin.product-sync/import-finish [app-atom import-finish-response]
  (cond
    (:success import-finish-response)
    (do
      (reset! (cmn/process-cursor app-atom) nil)
      (urlstate/redirect app-atom "product-sync" "")
      (toaster/open-toast app-atom :success "Smashed it!" {})
      (toaster/open-overlay app-atom :admin.product-sync/import))

    :else
    (toaster/open-toast app-atom :error (:msg import-finish-response) {})))